import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { Plus, Eye, EyeOff, Loader2, Pencil, KeyRound, Trash2, Power, ShieldCheck } from "lucide-react";

const PERM_LABELS = {
  orcamentos: "Orçamentos",
  reservas: "Reservas",
  corridas: "Corridas",
  motoristas: "Motoristas",
  dashboard: "Dashboard",
  configuracoes: "Configurações",
  precos: "Preços e Tarifas",
  logs: "Histórico/Logs",
  termos: "Termos",
  notificacoes: "Notificações",
};

const PERFIS = [
  { key: "financeiro", label: "ADM FINANCEIRO", perms: ["precos", "dashboard", "reservas"] },
  { key: "motoristas", label: "ADM MOTORISTAS", perms: ["motoristas", "termos"] },
  { key: "operacional", label: "ADM OPERACIONAL", perms: ["corridas", "orcamentos", "reservas"] },
];
const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

function PermChecks({ value, onChange, prefix }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`${prefix}-permissoes`}>
      {Object.entries(PERM_LABELS).map(([k, l]) => (
        <button
          key={k}
          type="button"
          data-testid={`${prefix}-perm-${k}`}
          onClick={() => toggle(k)}
          className={`py-2.5 rounded-xl border-2 text-xs font-bold active:scale-95 transition-all ${
            value.includes(k) ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-500"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function SenhaField({ value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        className={`${inputCls} pr-12`}
        placeholder="Senha (mín. 6 caracteres)"
        value={value}
        onChange={onChange}
      />
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy">
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
}

function Gerenciar({ user }) {
  const [admins, setAdmins] = useState(null);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState({ login: "", email: "", nome: "", senha: "", nivel: "adm", permissoes: [] });
  const [editando, setEditando] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data.admins)).catch(() => setAdmins([]));
  useEffect(() => { load(); }, []);

  const criar = async () => {
    setBusy(true);
    try {
      await api.post("/admin/admins", form);
      toast.success("Administrador criado!");
      setNovo(false);
      setForm({ login: "", email: "", nome: "", senha: "", nivel: "adm", permissoes: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar");
    } finally {
      setBusy(false);
    }
  };

  const salvarEdicao = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/admins/${editando.admin_id}`, {
        nome: editando.nome, email: editando.email, nivel: editando.nivel, permissoes: editando.permissoes,
      });
      toast.success("Administrador atualizado!");
      setEditando(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setBusy(false);
    }
  };

  const toggleAtivo = async (a) => {
    try {
      await api.patch(`/admin/admins/${a.admin_id}`, { ativo: !a.ativo });
      toast.success(a.ativo ? "Administrador desativado" : "Administrador ativado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    }
  };

  const redefinir = async (a) => {
    const nova = window.prompt(`Nova senha para ${a.nome} (mín. 6 caracteres):`);
    if (!nova) return;
    try {
      await api.post(`/admin/admins/${a.admin_id}/redefinir-senha`, { nova_senha: nova });
      toast.success("Senha redefinida!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    }
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir o administrador ${a.nome}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/admins/${a.admin_id}`);
      toast.success("Administrador excluído");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir");
    }
  };

  if (user?.admin_nivel !== "super") {
    return (
      <AdminShell user={user} title="Administradores">
        <p className="px-4 mt-6 text-sm text-red-600" data-testid="admins-restrito">
          Área exclusiva do Super ADM.
        </p>
      </AdminShell>
    );
  }

  return (
    <AdminShell user={user} title="Gerenciar ADM">
      <div className="px-4 mt-4 space-y-3" data-testid="admin-admins-page">
        <button
          data-testid="admin-novo-btn"
          onClick={() => setNovo((v) => !v)}
          className="w-full py-3.5 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <Plus className="w-4 h-4" /> {novo ? "CANCELAR" : "NOVO ADMINISTRADOR"}
        </button>

        {novo && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2.5" data-testid="admin-create-form">
            <p className="text-xs font-bold text-navy uppercase tracking-wide">Novo administrador</p>
            <input data-testid="admin-form-nome" className={inputCls} placeholder="Nome completo" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            <input data-testid="admin-form-login" className={inputCls} placeholder="Login" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} />
            <input data-testid="admin-form-email" className={inputCls} placeholder="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <SenhaField testid="admin-form-senha" value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} />
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Nível de acesso</p>
            <div className="grid grid-cols-2 gap-2">
              {[["adm", "ADM"], ["super", "SUPER ADM"]].map(([v, l]) => (
                <button key={v} data-testid={`admin-form-nivel-${v}`} onClick={() => setForm({ ...form, nivel: v })}
                  className={`py-3 rounded-xl border-2 text-xs font-bold active:scale-95 transition-all ${
                    form.nivel === v ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-500"}`}>
                  {l}
                </button>
              ))}
            </div>
            {form.nivel === "adm" && (
              <>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Perfil rápido</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {PERFIS.map((p) => (
                    <button key={p.key} type="button" data-testid={`admin-form-perfil-${p.key}`}
                      onClick={() => setForm({ ...form, permissoes: [...p.perms] })}
                      className="py-2 rounded-xl border border-slate-200 bg-slate-50 text-[9px] font-extrabold text-navy active:scale-95 transition-transform">
                      {p.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Permissões do ADM</p>
                <PermChecks prefix="admin-form" value={form.permissoes} onChange={(p) => setForm({ ...form, permissoes: p })} />
              </>
            )}
            {form.nivel === "super" && (
              <p className="text-[11px] text-slate-400">Super ADM tem acesso total, incluindo gerenciar administradores.</p>
            )}
            <button data-testid="admin-form-salvar" onClick={criar} disabled={busy || !form.nome || !form.login || !form.email || form.senha.length < 6}
              className="w-full py-3.5 rounded-2xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-50">
              {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "CRIAR ADMINISTRADOR"}
            </button>
          </div>
        )}

        {admins === null && <div className="flex justify-center py-14"><Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /></div>}
        {(admins || []).map((a) => (
          <div key={a.admin_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`admin-row-${a.admin_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ShieldCheck className={`w-4 h-4 ${a.nivel === "super" ? "text-[#F5B301]" : "text-slate-400"}`} />
                <p className="font-heading font-bold text-navy text-sm">{a.nome}</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${a.ativo ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`} data-testid={`admin-status-${a.admin_id}`}>
                {a.ativo ? "ATIVO" : "INATIVO"}
              </span>
            </div>
            <p className="text-xs text-slate-500">@{a.login} · {a.email}</p>
            <p className="text-[11px] mt-1" data-testid={`admin-nivel-${a.admin_id}`}>
              <span className={`font-extrabold ${a.nivel === "super" ? "text-[#F5B301]" : "text-navy"}`}>{a.nivel === "super" ? "SUPER ADM" : "ADM"}</span>
              {a.nivel !== "super" && (
                <span className="text-slate-400"> · {(a.permissoes || []).map((p) => PERM_LABELS[p] || p).join(", ") || "sem permissões"}</span>
              )}
            </p>

            {editando?.admin_id === a.admin_id ? (
              <div className="mt-3 space-y-2.5 border-t border-slate-100 pt-3" data-testid={`admin-edit-form-${a.admin_id}`}>
                <input data-testid={`admin-edit-nome-${a.admin_id}`} className={inputCls} value={editando.nome} onChange={(e) => setEditando({ ...editando, nome: e.target.value })} />
                <input data-testid={`admin-edit-email-${a.admin_id}`} className={inputCls} type="email" value={editando.email} onChange={(e) => setEditando({ ...editando, email: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  {[["adm", "ADM"], ["super", "SUPER ADM"]].map(([v, l]) => (
                    <button key={v} data-testid={`admin-edit-nivel-${v}-${a.admin_id}`} onClick={() => setEditando({ ...editando, nivel: v })}
                      className={`py-2.5 rounded-xl border-2 text-xs font-bold ${editando.nivel === v ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-500"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {editando.nivel === "adm" && (
                  <PermChecks prefix={`admin-edit-${a.admin_id}`} value={editando.permissoes} onChange={(p) => setEditando({ ...editando, permissoes: p })} />
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid={`admin-edit-salvar-${a.admin_id}`} onClick={salvarEdicao} disabled={busy}
                    className="py-2.5 rounded-xl bg-navy text-[#F5B301] text-xs font-extrabold">SALVAR</button>
                  <button data-testid={`admin-edit-cancelar-${a.admin_id}`} onClick={() => setEditando(null)}
                    className="py-2.5 rounded-xl bg-slate-100 text-slate-500 text-xs font-bold">CANCELAR</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-1.5 mt-2.5 flex-wrap">
                <button data-testid={`admin-editar-${a.admin_id}`} onClick={() => setEditando({ ...a })}
                  className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> EDITAR
                </button>
                <button data-testid={`admin-senha-${a.admin_id}`} onClick={() => redefinir(a)}
                  className="px-3 py-2 rounded-lg bg-gold-light border border-[#F5B301]/60 text-navy text-[10px] font-bold flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> REDEFINIR SENHA
                </button>
                <button data-testid={`admin-toggle-${a.admin_id}`} onClick={() => toggleAtivo(a)}
                  className={`px-3 py-2 rounded-lg text-[10px] font-bold flex items-center gap-1 ${a.ativo ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  <Power className="w-3 h-3" /> {a.ativo ? "DESATIVAR" : "ATIVAR"}
                </button>
                <button data-testid={`admin-excluir-${a.admin_id}`} onClick={() => excluir(a)}
                  className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-bold flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> EXCLUIR
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminShell>
  );
}

export default function AdminAdmins() {
  return <AdminGate>{(user) => <Gerenciar user={user} />}</AdminGate>;
}
